// =============================================================================
// SimSense Platform — Team Detail Page
// =============================================================================

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import { SessionCard } from "../components/SessionCard";
import { useToast } from "../components/Toast";
import { useConfirm } from "../components/ConfirmDialog";

interface Team {
  id: string;
  name: string;
  description: string | null;
  logoUrl: string | null;
  isPublic: boolean;
  ownerId: string;
  inviteToken: string | null;
  sharingPolicy: {
    shareTelemetry: boolean;
    shareSetups: boolean;
  };
}

type Tab = "activity" | "setups" | "members" | "settings";

export function TeamDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>("activity");
  
  const [team, setTeam] = useState<Team | null>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [activity, setActivity] = useState<any[]>([]);
  const [setups, setSetups] = useState<any[]>([]);
  
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [isOwner, setIsOwner] = useState(false);

  // Edit State [OT-119]
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editLogo, setEditLogo] = useState("");
  const [editIsPublic, setEditIsPublic] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();
  const confirmDialog = useConfirm();

  useEffect(() => {
    async function fetchTeamData() {
      try {
        const [teamData, membersData] = await Promise.all([
          api<Team>(`/api/teams/${id}`),
          api<any[]>(`/api/teams/${id}/members`)
        ]);
        setTeam(teamData);
        setMembers(membersData);
        setEditName(teamData.name);
        setEditDesc(teamData.description || "");
        setEditLogo(teamData.logoUrl || "");
        setEditIsPublic(teamData.isPublic);
        
        // Activity & Setups
        const [activityData, setupsData] = await Promise.all([
          api<any[]>(`/api/teams/${id}/activity`),
          api<any[]>(`/api/teams/${id}/setups`)
        ]);
        setActivity(activityData);
        setSetups(setupsData);
        
        const me = await api<any>("/api/auth/me");
        if (teamData.ownerId === me.userId) {
          setIsOwner(true);
        }
      } catch (err) {
        console.error("Failed to fetch team details", err);
      } finally {
        setLoading(false);
      }
    }
    fetchTeamData();
  }, [id]);

  const handleUpdateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!team) return;
    setIsSaving(true);
    try {
      const updated = await api<Team>(`/api/teams/${id}`, {
        method: "PUT",
        body: JSON.stringify({
          name: editName,
          description: editDesc,
          logoUrl: editLogo,
          isPublic: editIsPublic
        })
      });
      setTeam(updated);
      toast.success("Team updated successfully!");
    } catch (err) {
      toast.error("Failed to update team.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdatePolicy = async (key: "shareTelemetry" | "shareSetups", val: boolean) => {
    if (!team) return;
    const currentPolicy = team.sharingPolicy || { shareTelemetry: true, shareSetups: true };
    const newPolicy = { ...currentPolicy, [key]: val };
    try {
      const updated = await api<Team>(`/api/teams/${id}`, {
        method: "PUT",
        body: JSON.stringify({ sharingPolicy: newPolicy })
      });
      setTeam(updated);
    } catch (err) {
      toast.error("Failed to update policy.");
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    try {
      await api(`/api/teams/${id}/invites`, {
        method: "POST",
        body: JSON.stringify({ inviteeEmail: inviteEmail })
      });
      toast.success("Invitation sent!");
      setInviteEmail("");
    } catch (err) {
      toast.error("Failed to send invitation.");
    }
  };

  const handleGenerateToken = async () => {
    if (!team) return;
    try {
      const result = await api<{ inviteToken: string }>(`/api/teams/${id}/generate-invite-token`, {
        method: "POST"
      });
      setTeam({ ...team, inviteToken: result.inviteToken });
    } catch (err) {
      toast.error("Failed to generate token.");
    }
  };

  const handleDeleteTeam = async () => {
    const ok = await confirmDialog({ title: "Delete Team", message: "Are you sure you want to delete this team? This action is permanent.", danger: true, confirmLabel: "Delete Team" });
    if (!ok) return;
    try {
      await api(`/api/teams/${id}`, { method: "DELETE" });
      navigate("/teams");
    } catch (err) {
      toast.error("Failed to delete team.");
    }
  };

  if (loading) return <div style={{ textAlign: "center", padding: "80px" }}>Loading team details...</div>;
  if (!team) return <div style={{ textAlign: "center", padding: "80px" }}>Team not found.</div>;

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
      {/* Breadcrumbs */}
      <div style={{ marginBottom: "24px" }}>
        <button onClick={() => navigate("/teams")} style={{ background: "none", border: "none", color: "var(--accent)", cursor: "pointer", fontSize: "14px", fontWeight: 600, display: "flex", alignItems: "center", gap: "4px" }}>
          <span>←</span> Back to Teams
        </button>
      </div>

      {/* Header */}
      <div style={{ display: "flex", gap: "32px", alignItems: "center", marginBottom: "40px" }}>
          {team.logoUrl ? (
            <img src={team.logoUrl} alt={team.name} style={{ width: "120px", height: "120px", borderRadius: "20px", border: "1px solid var(--border)", objectFit: "cover" }} />
          ) : (
            <div style={{ fontSize: "64px", background: "var(--card)", width: "120px", height: "120px", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "20px", border: "1px solid var(--border)", flexShrink: 0 }}>🏁</div>
          )}
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
              <h2 style={{ fontSize: "40px", margin: 0, color: "#fff" }}>{team.name}</h2>
              <span style={{ 
                padding: "2px 8px", 
                borderRadius: "4px", 
                fontSize: "10px", 
                fontWeight: 700, 
                textTransform: "uppercase", 
                background: team.isPublic ? "var(--good-dim)" : "var(--border)",
                color: team.isPublic ? "var(--good)" : "var(--text3)",
                border: "1px solid",
                borderColor: team.isPublic ? "rgba(34, 197, 94, 0.2)" : "var(--border)"
              }}>
                {team.isPublic ? "Public" : "Private"}
              </span>
            </div>
            <p style={{ color: "var(--text2)", fontSize: "16px", maxWidth: "600px", lineHeight: "1.6" }}>{team.description || "No description provided."}</p>
            <div style={{ display: "flex", gap: "24px", marginTop: "20px" }}>
               <div>
                 <div style={{ fontSize: "10px", color: "var(--text3)", textTransform: "uppercase", marginBottom: "4px" }}>Members</div>
                 <div style={{ fontSize: "18px", fontWeight: 700 }}>{members.length}</div>
               </div>
               <div>
                 <div style={{ fontSize: "10px", color: "var(--text3)", textTransform: "uppercase", marginBottom: "4px" }}>Recent Activity</div>
                 <div style={{ fontSize: "18px", fontWeight: 700 }}>{activity.length} runs</div>
               </div>
            </div>
          </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: "32px", borderBottom: "1px solid var(--border)", marginBottom: "32px" }}>
        <TabButton active={activeTab === "activity"} onClick={() => setActiveTab("activity")} label="Activity Feed" />
        <TabButton active={activeTab === "setups"} onClick={() => setActiveTab("setups")} label="Team Setups" />
        <TabButton active={activeTab === "members"} onClick={() => setActiveTab("members")} label="Members" />
        {isOwner && <TabButton active={activeTab === "settings"} onClick={() => setActiveTab("settings")} label="Management" />}
      </div>

      <div style={{ minHeight: "400px" }}>
        
        {/* TAB: Activity */}
        {activeTab === "activity" && (
          <section>
            {activity.length === 0 ? (
              <EmptyState icon="⏱️" title="No Recent Activity" message="Sessions uploaded by team members will appear here." />
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))", gap: "20px" }}>
                {activity.map(session => (
                  <SessionCard key={session.id} session={session} showDriver />
                ))}
              </div>
            )}
          </section>
        )}

        {/* TAB: Setups */}
        {activeTab === "setups" && (
          <section>
            {team.sharingPolicy?.shareSetups === false ? (
              <div style={{ padding: "48px", textAlign: "center", background: "rgba(255, 107, 107, 0.05)", borderRadius: "var(--radius)", border: "1px dashed var(--bad)" }}>
                <div style={{ fontSize: "32px", marginBottom: "12px" }}>🔒</div>
                <h3 style={{ color: "var(--bad)", marginBottom: "8px" }}>Sharing Restricted</h3>
                <div style={{ color: "var(--text2)" }}>Setup sharing is currently disabled by the team owner.</div>
              </div>
            ) : setups.length === 0 ? (
              <EmptyState icon="⚙️" title="No Shared Setups" message="Setups uploaded by team members will appear here." />
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "12px" }}>
                {setups.map(setup => (
                  <div key={setup.id} style={{ background: "var(--card)", padding: "20px", borderRadius: "12px", border: "1px solid var(--border)", display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", gap: "24px" }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
                        <h4 style={{ margin: 0, fontSize: "18px", color: "#fff" }}>{setup.setupName}</h4>
                        <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 6px", background: "rgba(255,255,255,0.05)", borderRadius: "4px", textTransform: "uppercase" }}>{setup.carName}</span>
                      </div>
                      <div style={{ fontSize: "13px", color: "var(--text3)" }}>
                        Uploaded by <span style={{ color: "var(--accent)" }}>{setup.driverName || "Member"}</span> • {new Date(setup.updatedAt).toLocaleDateString()}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "12px" }}>
                       <button 
                         onClick={() => navigate(`/setup/builder?id=${setup.id}`)}
                         style={{ padding: "8px 16px", background: "rgba(255,255,255,0.05)", border: "1px solid var(--border)", color: "#fff", borderRadius: "6px", fontSize: "13px", fontWeight: 600, cursor: "pointer" }}
                       >
                         View Setup
                       </button>
                       <a 
                         href={`/api/setup/my-setups/${setup.id}/export`}
                         style={{ padding: "8px 16px", background: "var(--accent)", color: "#fff", border: "none", borderRadius: "6px", fontSize: "13px", fontWeight: 700, textDecoration: "none" }}
                       >
                         Download .yaml
                       </a>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* TAB: Members */}
        {activeTab === "members" && (
          <section style={{ maxWidth: "800px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
               {members.map(m => (
                 <div key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", background: "var(--card)", borderRadius: "12px", border: "1px solid var(--border)" }}>
                   <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
                      <div style={{ width: "48px", height: "48px", borderRadius: "50%", background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: "18px" }}>
                        {m.user.displayName?.[0] || "?"}
                      </div>
                      <div>
                        <div style={{ fontSize: "16px", fontWeight: 600, color: "#fff" }}>{m.user.displayName || "Unknown User"}</div>
                        <div style={{ fontSize: "13px", color: "var(--text3)" }}>{m.user.email}</div>
                      </div>
                   </div>
                   <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                     <span style={{ fontSize: "11px", fontWeight: 700, padding: "4px 8px", background: "rgba(255,255,255,0.05)", border: "1px solid var(--border)", borderRadius: "6px", textTransform: "uppercase", color: "var(--text2)" }}>{m.role}</span>
                   </div>
                 </div>
               ))}
            </div>
          </section>
        )}

        {/* TAB: Settings (Management) */}
        {activeTab === "settings" && isOwner && (
          <section style={{ maxWidth: "1000px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "32px" }}>
              
              <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
                {/* Team Info */}
                <form onSubmit={handleUpdateTeam} style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
                  <h3 style={{ marginBottom: "20px", fontSize: "18px" }}>Team Profile</h3>
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <div>
                      <label style={labelStyle}>Team Name</label>
                      <input type="text" value={editName} onChange={e => setEditName(e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Description</label>
                      <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} style={{ ...inputStyle, height: "100px", resize: "none" }} />
                    </div>
                    <div>
                      <label style={labelStyle}>Logo URL</label>
                      <input type="text" value={editLogo} onChange={e => setEditLogo(e.target.value)} style={inputStyle} placeholder="https://..." />
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <input 
                        type="checkbox" 
                        id="isPublic" 
                        checked={editIsPublic} 
                        onChange={e => setEditIsPublic(e.target.checked)} 
                        style={{ width: "18px", height: "18px" }}
                      />
                      <label htmlFor="isPublic" style={{ fontSize: "14px", fontWeight: 500, cursor: "pointer" }}>Make team public</label>
                    </div>
                    <button type="submit" disabled={isSaving} style={{ ...btnStyle, background: "var(--accent)", padding: "12px", width: "100%", fontSize: "14px", fontWeight: 700, marginTop: "8px" }}>
                      {isSaving ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </form>

                {/* Dangerous Zone */}
                <div style={{ background: "rgba(248, 113, 113, 0.05)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid rgba(248, 113, 113, 0.2)" }}>
                  <h3 style={{ marginBottom: "12px", fontSize: "16px", color: "#f87171" }}>Danger Zone</h3>
                  <p style={{ fontSize: "13px", color: "var(--text2)", marginBottom: "20px" }}>Once you delete a team, there is no going back. Please be certain.</p>
                  <button onClick={handleDeleteTeam} style={{ ...btnStyle, background: "#ef4444", padding: "10px 20px", fontWeight: 700 }}>
                    Delete Team
                  </button>
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "32px" }}>
                {/* Policies */}
                <div style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
                  <h3 style={{ marginBottom: "20px", fontSize: "18px" }}>Sharing Policies</h3>
                  <p style={{ fontSize: "14px", color: "var(--text2)", marginBottom: "24px" }}>
                    Set global restrictions for team members. These policies override individual user preferences while they are active in the team.
                  </p>
                  
                  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                    <PolicyToggle 
                      label="Enable Telemetry Aggregation" 
                      description="Allow members to see each other's runs in the Activity Feed."
                      enabled={team.sharingPolicy?.shareTelemetry ?? true}
                      disabled={false}
                      onChange={(v) => handleUpdatePolicy("shareTelemetry", v)}
                    />
                    <PolicyToggle 
                      label="Enable Setup Sharing" 
                      description="Allow members to download and view setups from fellow team members."
                      enabled={team.sharingPolicy?.shareSetups ?? true}
                      disabled={false}
                      onChange={(v) => handleUpdatePolicy("shareSetups", v)}
                    />
                  </div>
                </div>

                {/* Recruitment */}
                <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
                  <div style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
                      <h4 style={{ marginBottom: "16px", fontSize: "16px" }}>Invite by Email</h4>
                      <form onSubmit={handleInvite} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                        <input 
                          type="email" 
                          value={inviteEmail}
                          onChange={e => setInviteEmail(e.target.value)}
                          placeholder="driver@example.com"
                          style={{ padding: "12px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "8px", color: "#fff", fontSize: "14px" }}
                        />
                        <button type="submit" style={{ padding: "12px", background: "var(--accent)", color: "#fff", border: "none", borderRadius: "8px", fontWeight: 700, cursor: "pointer", fontSize: "14px" }}>Send Invitation</button>
                      </form>
                  </div>

                  <div style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
                      <h4 style={{ marginBottom: "16px", fontSize: "16px" }}>Invite Link</h4>
                      {team.inviteToken ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                          <div style={{ fontSize: "12px", color: "var(--text2)" }}>Anyone with this link can join:</div>
                          <input 
                            readOnly 
                            value={`${window.location.origin}/teams/join/${team.inviteToken}`}
                            onClick={(e) => (e.target as HTMLInputElement).select()}
                            style={{ width: "100%", padding: "12px", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: "8px", color: "var(--accent)", fontSize: "12px", fontVariantNumeric: "tabular-nums" }}
                          />
                          <button onClick={handleGenerateToken} style={{ background: "none", border: "none", color: "var(--text3)", fontSize: "12px", cursor: "pointer", textAlign: "left" }}>Regenerate Link</button>
                        </div>
                      ) : (
                        <button 
                          onClick={handleGenerateToken}
                          style={{ width: "100%", padding: "12px", background: "rgba(255,255,255,0.05)", border: "1px dashed var(--border)", borderRadius: "8px", color: "#fff", cursor: "pointer", fontSize: "14px", fontWeight: 600, height: "100%" }}
                        >
                          Enable Invite Link
                        </button>
                      )}
                  </div>
                </div>
              </div>

            </div>
          </section>
        )}

      </div>
    </div>
  );
}

function TabButton({ active, label, onClick }: { active: boolean, label: string, onClick: () => void }) {
  return (
    <button 
      onClick={onClick}
      style={{
        padding: "12px 0",
        background: "none",
        border: "none",
        borderBottom: active ? "2px solid var(--accent)" : "2px solid transparent",
        color: active ? "#fff" : "var(--text3)",
        fontWeight: active ? 700 : 500,
        fontSize: "15px",
        cursor: "pointer",
        transition: "all 0.2s"
      }}
    >
      {label}
    </button>
  );
}

function EmptyState({ icon, title, message }: { icon: string, title: string, message: string }) {
  return (
    <div style={{ padding: "80px 24px", textAlign: "center", background: "var(--card)", borderRadius: "var(--radius)", border: "1px dashed var(--border)" }}>
      <div style={{ fontSize: "48px", marginBottom: "16px" }}>{icon}</div>
      <h3 style={{ marginBottom: "8px", color: "#fff" }}>{title}</h3>
      <p style={{ color: "var(--text2)", fontSize: "14px" }}>{message}</p>
    </div>
  );
}

function PolicyToggle({ label, description, enabled, disabled, onChange }: { label: string, description: string, enabled: boolean, disabled: boolean, onChange: (v: boolean) => void }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", background: "rgba(255,255,255,0.02)", borderRadius: "12px", border: "1px solid rgba(255,255,255,0.03)" }}>
      <div style={{ flex: 1, paddingRight: "24px" }}>
        <div style={{ fontSize: "15px", fontWeight: 600, marginBottom: "4px", color: "#fff" }}>{label}</div>
        <div style={{ fontSize: "12px", color: "var(--text3)" }}>{description}</div>
      </div>
      <button 
        disabled={disabled}
        onClick={() => onChange(!enabled)}
        style={{
          width: "48px",
          height: "24px",
          borderRadius: "12px",
          background: enabled ? "var(--accent)" : "var(--border)",
          border: "none",
          position: "relative",
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "background 0.2s",
          opacity: disabled ? 0.5 : 1
        }}
      >
        <div style={{
          width: "20px",
          height: "20px",
          borderRadius: "50%",
          background: "#fff",
          position: "absolute",
          top: "2px",
          left: enabled ? "26px" : "2px",
          transition: "left 0.2s"
        }} />
      </button>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "10px",
  color: "var(--text3)",
  textTransform: "uppercase",
  marginBottom: "6px",
  fontWeight: 700
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px",
  background: "var(--bg)",
  border: "1px solid var(--border)",
  borderRadius: "6px",
  color: "#fff",
  fontSize: "13px"
};

const btnStyle = {
  padding: "6px 12px",
  background: "var(--border)",
  border: "none",
  color: "#fff",
  borderRadius: "4px",
  fontSize: "11px",
  cursor: "pointer"
};
